import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Send, X } from 'lucide-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { audioDB } from '@/lib/storage/audioDB';
import { mergeStoredRecordings } from '@/lib/storage/recordings-merge';
import { type PatientOut, usePatients } from '../api/patients-api';
import { AudioBar } from '@/components/encounter/AudioBar';
import { useCreateEncounter } from '../api/encounters-api';
import { RecordingPanel } from '@/components/encounter/RecordingPanel';
import { type RecordingsListRef } from '@/components/audio/RecordingsList';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { AssignDocumentPopover } from '../components/AssignDocumentPopover';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';

/**
 * Quick Start — patientless recording surface (Plan 07).
 *
 * Lets a clinician pick a template, capture audio, and submit without
 * choosing a patient first. The submission produces an orphan encounter
 * + generated document that lands in the Inbox for adjudication.
 *
 * If the user *does* assign a patient up-front, the flow falls through
 * to the existing chart-bound encounter behavior — same API, same
 * `/encounters/$encounterId` redirect on success.
 */
export function QuickStartPage() {
  const setPageHeader = usePageHeaderSetter();
  const navigate = useNavigate();
  const recordingsListRef = useRef<RecordingsListRef>(null);

  const { formDraft, setFormDraft, clearSession, recordingMeta, setOriginRoute } = useEncounterSessionStore();
  const { patientId: preselectedPatientId } = useSearch({ from: '/_authenticated/quick-start' });
  const { data: patientsData } = usePatients({ limit: 100 });

  const [templateId, setTemplateId] = useState(formDraft.template_id ?? '');
  const [title, setTitle] = useState(formDraft.title ?? '');
  const [assignedPatient, setAssignedPatient] = useState<PatientOut | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const preselectAppliedRef = useRef(false);
  // Serialize recovery scans so a tight async race between an IDB read
  // and a React re-render can't run two concurrent merges and double-add
  // the same blob. The helper is pure; the mutex is on the caller.
  const recoveryInFlightRef = useRef(false);
  // Mirror `title` into a ref so handleSubmit can read it without listing
  // `title` in its useCallback deps — otherwise every keystroke rebuilds
  // the callback and re-runs the page-header useEffect.
  const titleRef = useRef(title);
  titleRef.current = title;

  // Destructure the stable `mutateAsync`. The whole mutation object's
  // identity changes every render (because it carries fresh isPending /
  // data state), which would destabilize `handleSubmit` and re-fire the
  // page-header useEffect, producing a "Maximum update depth exceeded"
  // loop. `mutateAsync` itself is stable inside react-query.
  const { mutateAsync: submitEncounter } = useCreateEncounter();

  // Persist Quick Start origin so the global recorder pill returns here.
  useEffect(() => {
    setOriginRoute('/quick-start');
  }, [setOriginRoute]);

  // Pre-assign the patient when the route was opened with ?patientId=…
  // (e.g. the Personal redirect from /encounters/new?patientId=…).
  // We only apply this once so manually clearing the badge sticks, AND we
  // mark it applied even when the candidate isn't found — otherwise the
  // effect re-fires on every background refetch for a stale/invalid id.
  useEffect(() => {
    if (preselectAppliedRef.current || !preselectedPatientId) return;
    if (!patientsData) return;
    const candidate = (patientsData.data ?? []).find((p) => p.id === preselectedPatientId);
    if (candidate) setAssignedPatient(candidate);
    preselectAppliedRef.current = true;
  }, [preselectedPatientId, patientsData]);

  // Recover any in-flight recordings from IndexedDB after a page refresh
  // or pill-stop navigation — same behavior as NewEncounterPage.
  // Single source of truth for the recordings list: audioDB. The recorder
  // writes the blob there in `stopRecording()` BEFORE flipping
  // `recordingMeta.status` to `'stopped'`, so this effect picks it up
  // exactly once. ``mergeStoredRecordings`` is a pure helper covered by
  // unit tests in ``recordings-merge.test.ts``.
  useEffect(() => {
    if (recordingMeta.status !== 'stopped' && recordingMeta.status !== 'idle') {
      return;
    }
    if (recoveryInFlightRef.current) return;
    recoveryInFlightRef.current = true;
    async function recoverRecordings() {
      try {
        const stored = await audioDB.getAll();
        const existing = recordingsListRef.current?.getRecordings() ?? [];
        for (const rec of mergeStoredRecordings(existing, stored)) {
          recordingsListRef.current?.addRecording(rec);
        }
      } finally {
        recoveryInFlightRef.current = false;
      }
    }
    void recoverRecordings();
  }, [recordingMeta.status]);

  // Sync template selection into the session store so the in-progress
  // pick survives a navigation away & back.
  const handleTemplateChange = useCallback(
    (id: string) => {
      setTemplateId(id);
      setFormDraft({ template_id: id });
    },
    [setFormDraft],
  );

  const handleTitleChange = useCallback(
    (value: string) => {
      setTitle(value);
      setFormDraft({ title: value });
    },
    [setFormDraft],
  );

  const handleSubmit = useCallback(async () => {
    if (!templateId) {
      toast.error('Pick a template first');
      return;
    }
    const recordings = recordingsListRef.current?.getRecordings() ?? [];
    if (recordings.length === 0) {
      toast.error('At least one recording is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const audioFiles = recordings
        .filter((r) => r.blob != null)
        .map(
          (r, idx) =>
            new File([r.blob!], r.name || `recording-${idx}.webm`, {
              type: r.blob!.type || 'audio/webm',
            }),
        );
      const trimmedTitle = titleRef.current.trim();
      const clinicalContext: Record<string, unknown> = {
        template_id: templateId,
        ...(trimmedTitle ? { title: trimmedTitle } : {}),
      };

      const result = await submitEncounter({
        files: audioFiles,
        status: 'submitted',
        encounter_data: {
          patient_id: assignedPatient?.id ?? null,
          clinical_context: clinicalContext,
          encounter_date: format(new Date(), 'yyyy-MM-dd'),
          // Encounter-type only makes sense once a patient is in play; an
          // orphan Quick Start encounter is internal scaffolding for the
          // generated document.
          encounter_type: assignedPatient ? 'initial' : null,
          ...(trimmedTitle ? { additional_context: { title: trimmedTitle } } : {}),
        },
      });

      await audioDB.deleteAll();
      clearSession();

      if (assignedPatient) {
        toast.success('Encounter submitted — generating report.');
        navigate({
          to: '/encounters/$encounterId',
          params: { encounterId: result.id },
        });
      } else {
        toast.success('Generating — your report will appear in the Inbox shortly.');
        navigate({ to: '/documents' });
      }
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to submit. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [templateId, assignedPatient, submitEncounter, clearSession, navigate]);

  useEffect(() => {
    setPageHeader({
      title: 'Quick Start',
      actions: (
        <Button disabled={isSubmitting || !templateId} onClick={() => void handleSubmit()}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="hidden md:inline">{isSubmitting ? 'Submitting…' : 'Submit'}</span>
        </Button>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, isSubmitting, templateId, handleSubmit]);

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Block the page during the multipart upload — large audio files can
          take a few seconds and the user shouldn't be tempted to click away. */}
      {isSubmitting && (
        <div
          aria-busy="true"
          aria-live="polite"
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 aria-hidden className="h-8 w-8 animate-spin text-primary" />
            <p className="text-lg font-medium">
              {assignedPatient
                ? 'Submitting encounter…'
                : 'Uploading recording — your report will appear in the Inbox shortly.'}
            </p>
          </div>
        </div>
      )}

      {/*
        Don't add to the in-memory list on stop — `stopRecording()` already
        persists the blob to audioDB BEFORE flipping `recordingMeta.status`
        to `'stopped'`, which triggers the recovery effect above. Doing
        both produced duplicate rows with mismatched ids (one
        crypto.randomUUID(), one assigned by audioDB), so removing one X
        button left the other pointing at the same blob.
      */}
      <AudioBar />

      <div className="space-y-2 rounded-lg border bg-card p-4">
        <Label htmlFor="quick-start-title" className="text-sm">
          Title
        </Label>
        <Input
          id="quick-start-title"
          maxLength={200}
          value={title}
          placeholder="e.g. Daisy follow-up — May 13"
          onChange={(e) => handleTitleChange(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Optional. Used as the generated report&rsquo;s name in your Inbox or the patient chart.
        </p>
      </div>

      <RecordingPanel
        templateId={templateId}
        recordingsListRef={recordingsListRef}
        onTemplateChange={handleTemplateChange}
      />

      {/* Optional patient assignment — when omitted, the report goes to Inbox. */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          {assignedPatient
            ? 'This recording will be filed in the patient chart.'
            : 'No patient selected — the report will land in your Inbox.'}
        </p>

        {assignedPatient ? (
          <Badge variant="secondary" className="gap-2 px-3 py-1.5 text-sm">
            {patientLabel(assignedPatient)}
            <button
              type="button"
              className="-mr-1 hover:opacity-70"
              aria-label="Clear assigned patient"
              onClick={() => setAssignedPatient(null)}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ) : (
          <AssignDocumentPopover
            disabled={isSubmitting}
            onAssign={(patient) => setAssignedPatient(patient)}
            trigger={
              <Button size="sm" variant="outline">
                Assign to a patient
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
}

/**
 * Pull a meaningful error message out of an axios failure. FastAPI's
 * 422 returns ``detail`` as a list of validation errors; 400/409/500
 * return ``detail`` as a string. Both shapes are surfaced with a
 * sensible fallback when the response is missing or oddly shaped.
 */
function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string; loc?: unknown[] };
      if (first?.msg) {
        const path = Array.isArray(first.loc) ? first.loc.slice(-1).join('.') : '';
        return path ? `${path}: ${first.msg}` : first.msg;
      }
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function patientLabel(patient: PatientOut): string {
  const meta = patient.metadata_;
  const name = `${meta?.last_name ?? ''}, ${meta?.first_name ?? ''}`.trim().replace(/^,\s*$/, '');
  const display = name || patient.medical_record_number;
  return `${display} · MRN ${patient.medical_record_number}`;
}
